import { LitElement, html } from 'lit';
import { API_ENDPOINT } from './constants';

export class CharacterEditor extends LitElement {
  createRenderRoot() {
    return this;
  }

  static properties = {
    characterId: { type: String },
    label: { type: String },
    gender: { type: String },
    physicalAttrs: { type: Array },
    images: { type: Array },
    filesToUpload: { type: Array },
    isLoading: { type: Boolean },
    errorMessage: { type: String },
  };

  constructor() {
    super();
    this.characterId = null;
    this.label = '';
    this.gender = 'female';
    this.physicalAttrs = [];
    this.images = [];
    this.filesToUpload = [];
    this.isLoading = false;
    this.errorMessage = '';
  }

  connectedCallback() {
    super.connectedCallback();
    const params = new URLSearchParams(window.location.search);
    if (params.has('id')) {
      this.characterId = params.get('id');
      this.loadCharacter(this.characterId);
    }
  }

  async loadCharacter(id) {
    this.isLoading = true;
    this.errorMessage = '';
    try {
      const res = await fetch(`${API_ENDPOINT}/api/characters/${id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load character');
      const char = await res.json();
      this.label = char.label;
      this.gender = char.gender;
      this.physicalAttrs = char.physical_attrs?.split(',') || [];
      await this.loadCharacterImages(id);
    } catch (err) {
      this.errorMessage = 'Failed to load character data';
      console.error('Error loading character:', err);
    } finally {
      this.isLoading = false;
    }
  }

  async loadCharacterImages(id) {
    try {
      const res = await fetch(`${API_ENDPOINT}/api/characters/${id}/images`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load images');
      this.images = await res.json();
    } catch (err) {
      console.error('Error loading character images:', err);
    }
  }

  updateProperty(prop, e) {
    this[prop] = e.target.value;
  }

  handleFilesSelected(e) {
    this.filesToUpload = Array.from(e.target.files);
    this.errorMessage = '';
  }

  async saveCharacter() {
    if (!this.label.trim()) {
      this.errorMessage = 'Please provide a character label';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    
    const method = this.characterId ? 'PUT' : 'POST';
    const url = this.characterId
      ? `${API_ENDPOINT}/api/characters/${this.characterId}`
      : `${API_ENDPOINT}/api/characters`;

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          label: this.label,
          gender: this.gender,
          physical_attrs: this.physicalAttrs,
        }),
      });

      if (!res.ok) throw new Error('Failed to save character');
      const data = await res.json();
      this.characterId = this.characterId || data.id;

      if (this.filesToUpload.length > 0) {
        const formData = new FormData();
        this.filesToUpload.forEach(file => formData.append('images', file));
        
        const uploadRes = await fetch(`${API_ENDPOINT}/api/characters/${this.characterId}/images/batch`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        
        if (!uploadRes.ok) throw new Error('Failed to upload images');
        await this.loadCharacterImages(this.characterId);
      }

      window.history.pushState({}, '', `/try-on?charId=${this.characterId}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (err) {
      this.errorMessage = err.message;
      console.error(err);
    } finally {
      this.isLoading = false;
    }
  }

  async deleteImage(imageId) {
    this.isLoading = true;
    this.errorMessage = '';
    try {
      const res = await fetch(`${API_ENDPOINT}/api/characters/${this.characterId}/images/${imageId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete image');
      this.images = this.images.filter(img => img.id !== imageId);
    } catch (err) {
      this.errorMessage = 'Failed to delete image';
      console.error(err);
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    return html`
      <div class="min-h-screen bg-base-200 flex items-center justify-center p-4">
        <div class="w-full max-w-2xl">
          <div class="card bg-base-100 shadow-2xl">
            <div class="card-body">
              <h1 class="card-title text-2xl md:text-3xl font-bold mb-6">
                ${this.characterId ? 'Edit Your Model' : 'Create New Model'}
              </h1>

              ${this.errorMessage ? html`
                <div class="alert alert-error mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>${this.errorMessage}</span>
                </div>
              ` : ''}

              <div class="space-y-6">
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-semibold">Model Name</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Fitness Model"
                    class="input input-bordered w-full"
                    .value=${this.label}
                    @input=${(e) => this.updateProperty('label', e)}
                    ?disabled=${this.isLoading}
                  />
                </div>

                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-semibold">Upload Photos</span>
                    <span class="label-text-alt text-base-content/70">Multiple files supported</span>
                  </label>
                  <input
                    type="file"
                    multiple
                    @change=${this.handleFilesSelected}
                    class="file-input file-input-bordered file-input-primary w-full"
                    accept="image/*"
                    ?disabled=${this.isLoading}
                  />
                  <div class="mt-2 text-sm text-base-content/70">
                    Tip: Use clear, full-body shots in minimal clothing for best results
                  </div>
                </div>

                <button
                  class="btn btn-primary w-full ${this.isLoading ? 'loading' : ''}"
                  @click=${this.saveCharacter}
                  ?disabled=${this.isLoading}
                >
                  ${this.isLoading ? 'Saving...' : 'Save Model'}
                </button>
              </div>

              ${this.characterId && this.images.length > 0 ? html`
                <div class="divider my-8"></div>
                <h2 class="text-xl font-semibold mb-4">Current Photos</h2>
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  ${this.images.map(img => html`
                    <div class="relative group">
                      <img
                        src="${img.image_url}"
                        alt="Character image"
                        class="w-full h-40 object-cover rounded-lg"
                      />
                      <button
                        class="btn btn-error btn-sm absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        @click=${() => this.deleteImage(img.id)}
                        ?disabled=${this.isLoading}
                      >
                        ✕
                      </button>
                    </div>
                  `)}
                </div>
              ` : ''}
            </div>
          </div>
          
          <div class="text-center mt-4">
            <a href="/try-on" class="link link-neutral">Back to Try On</a>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('character-editor', CharacterEditor);
